import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useSuggestionsStore } from '@/hooks/useStores';
import { formatStoryDate } from '@/types/story';
import { suggestionStatusLabel } from '@/types/suggestion';
import { formatRelativeTime } from '@/utils/formatRelativeTime';

interface OverviewPendingSuggestionsCardProps {
  familyId: string;
  canShow: boolean;
  onViewAll: () => void;
}

const OverviewPendingSuggestionsCard: React.FC<OverviewPendingSuggestionsCardProps> = observer(
  ({ familyId, canShow, onViewAll }) => {
    const { t } = useTranslation('overview');
    const suggestionsStore = useSuggestionsStore();

    const loadSummary = useCallback(async () => {
      if (!canShow) return;
      await suggestionsStore.loadMyPendingSummary(familyId);
    }, [canShow, familyId, suggestionsStore]);

    useEffect(() => {
      void loadSummary();
    }, [loadSummary]);

    if (!canShow) return null;

    const pendingCount = suggestionsStore.pendingSummaryFamilyId === familyId ? suggestionsStore.pendingAuthorTotal : 0;
    const preview = suggestionsStore.pendingSummaryFamilyId === familyId ? suggestionsStore.pendingAuthorPreview : [];

    if (suggestionsStore.isLoadingPendingSummary) {
      return (
        <section className="overview-card" aria-labelledby="overview-pending-suggestions-title">
          <header className="overview-card__head">
            <h2 id="overview-pending-suggestions-title" className="overview-card__title">
              {t('pendingSuggestions.title')}
            </h2>
          </header>
          <p className="overview-card__empty">{t('pendingSuggestions.loading')}</p>
        </section>
      );
    }

    if (pendingCount <= 0) return null;

    return (
      <section className="overview-card" aria-labelledby="overview-pending-suggestions-title">
        <header className="overview-card__head">
          <h2 id="overview-pending-suggestions-title" className="overview-card__title">
            {t('pendingSuggestions.title')}
            <span className="overview-card__count">{pendingCount}</span>
          </h2>
          <button type="button" className="overview-card__more" onClick={onViewAll}>
            {t('pendingSuggestions.viewAll')}
          </button>
        </header>
        <div className="overview-card__body">
          {preview.map((item) => (
            <div key={item.id} className="review-item">
              <div>
                <div className="review-item__name">{item.title || t('pendingSuggestions.untitled')}</div>
                <div className="review-item__meta">
                  {suggestionStatusLabel(item.status)}
                  {' · '}
                  {formatRelativeTime(item.createdAt)}
                </div>
              </div>
              <div className="review-item__date">{formatStoryDate(item.createdAt)}</div>
            </div>
          ))}
        </div>
      </section>
    );
  }
);

export default OverviewPendingSuggestionsCard;
